import FW from '../fw/fw';
import Env from '../fw/env';
import Logger from '../fw/logger';
import InnerDefaults from '../fw/defaults/inner';
import Language from '../fw/defaults/language';
import CCManagement from '../accessibility/cc';
import VttJS from '../accessibility/vtt-js';
import ViewableObserver from './viewable-observer';
import Fps from '../streaming/fps';
import NativeHls from '../streaming/native-hls';
import AdsImaDai from '../ads/ima-dai';
import ThreeSixty from '../xr/360';
import MediaSessionMetadata from './media-session-metadata';
import Playlist from '../playlist/playlist';
import Related from '../playlist/related';
import AdScheduler from '../ads/ad-scheduler';
import AdBlocker from '../ads/ad-blocker';
import UIManagement from '../ui/ui';


export default class InitManagement {

  #rmp;
  #validatedLanguage = 'english';
  #originalAdSchedule = {};
  #originalAdTagUrl = '';
  #originalAdTagWaterfall = [];
  #imaDaiLive = false;
  #imaDaiVod = false;
  #imaDaiPod = false;
  #imaDaiUseNativeHls = false;
  #ccFilesNativeTextTracksLoadedCount = 0;
  #hasTranscripts = false;
  #durationchangeFn = null;
  #loadedmetadataFn = null;
  #waitingFn = null;
  #pauseFn = null;
  #playFn = null;
  #timeupdateFn = null;
  #iosExternalCCFilesWebkitbeginfullscreenFn = null;
  #iosExternalCCFilesWebkitendfullscreenFn = null;
  #onReplayLoadAdTagFn = null;
  #onEndedWaitForPlayFn = null;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  get hasTranscripts() {
    return this.#hasTranscripts;
  }

  get validatedLanguage() {
    return this.#validatedLanguage;
  }

  get ccFilesNativeTextTracksLoadedCount() {
    return this.#ccFilesNativeTextTracksLoadedCount;
  }

  set ccFilesNativeTextTracksLoadedCount(index) {
    this.#ccFilesNativeTextTracksLoadedCount = index;
  }

  get originalAdTagUrl() {
    return this.#originalAdTagUrl;
  }

  set originalAdTagUrl(url) {
    this.#originalAdTagUrl = url;
  }

  get originalAdTagWaterfall() {
    return this.#originalAdTagWaterfall;
  }

  set originalAdTagWaterfall(arrayUrls) {
    this.#originalAdTagWaterfall = arrayUrls;
  }

  get originalAdSchedule() {
    return this.#originalAdSchedule;
  }

  get imaDaiLive() {
    return this.#imaDaiLive;
  }

  get imaDaiVod() {
    return this.#imaDaiVod;
  }

  get imaDaiPod() {
    return this.#imaDaiPod;
  }

  get imaDaiUseNativeHls() {
    return this.#imaDaiUseNativeHls;
  }

  #createNativeSrc() {
    this.#rmp.rmpDomManagement.prepare();
    if (this.#rmp.readingFps) {
      const rmpFps = new Fps(this.#rmp);
      rmpFps.init();
    } else {
      if (this.#imaDaiUseNativeHls) {
        Logger.print('ima-dai', 'set up with native HLS playback');
        this.#rmp.rmpAdsImaDai = new AdsImaDai(this.#rmp);
        this.#rmp.rmpAdsImaDai.init();
      } else {
        this.#rmp.dom.video.src = this.#rmp.startingSrcUri;
      }
    }
    this.#rmp.rmpDomManagement.append();
  }

  #runNativeHls(hlsType) {
    this.#rmp.readingHls = true;
    switch (hlsType) {
      case 'fps':
        this.#rmp.preload = 'auto';
        this.#rmp.airplay = false;
        this.#rmp.readingFps = true;
        break;
      default:
        break;
    }
    this.#rmp.startingSrcUri = this.#rmp.src[hlsType];
    Logger.print('info', 'run native ' + hlsType + ' at URI ' + this.#rmp.startingSrcUri);
    this.#rmp.rmpNativeHls = new NativeHls(this.#rmp);
    this.#rmp.rmpNativeHls.init();
    this.#createNativeSrc();
  }

  #runWebM() {
    this.#rmp.readingMp4 = true;
    this.#rmp.readingWebM = true;
    this.#rmp.src.mp4 = this.#rmp.src.webm;
    this.#rmp.filteredInitialQuality = FW.filterInitialQuality(this.#rmp.initialQuality, this.#rmp.src.mp4);
    this.#rmp.startingSrcUri = this.#rmp.src.mp4[this.#rmp.filteredInitialQuality];
    Logger.print('info', `run WebM at URI ${this.#rmp.startingSrcUri} with index ${this.#rmp.filteredInitialQuality}`);
    this.#createNativeSrc();
  }

  #runMp4() {
    this.#rmp.readingMp4 = true;
    this.#rmp.readingWebM = false;
    this.#rmp.filteredInitialQuality = FW.filterInitialQuality(this.#rmp.initialQuality, this.#rmp.src.mp4);
    this.#rmp.startingSrcUri = this.#rmp.src.mp4[this.#rmp.filteredInitialQuality];
    Logger.print('info', `run MP4 at URI ${this.#rmp.startingSrcUri} with index ${this.#rmp.filteredInitialQuality}`);
    this.#createNativeSrc();
  }

  #runOutStream() {
    this.#rmp.hasLoadedMetadata = true;
    this.#rmp.readingOutstream = true;
    this.#rmp.rmpDomManagement.prepare();
    this.#rmp.rmpDomManagement.append();
  }

  #runAudioProgressiveDownload(type) {
    switch (type) {
      case 'm4a':
        this.#rmp.readingM4a = true;
        break;
      case 'mp3':
        this.#rmp.readingMp3 = true;
        break;
      case 'ogg':
        this.#rmp.readingOgg = true;
        break;
      default:
        break;
    }
    this.#rmp.startingSrcUri = this.#rmp.src[type];
    Logger.print('info', 'run HTML5 audio at URI ' + this.#rmp.startingSrcUri);
    this.#createNativeSrc();
  }

  #runHtml5Audio() {
    if (this.#rmp.hasM4a && (this.#rmp.okM4a || this.#rmp.pdCheckCodecsDone)) {
      this.#runAudioProgressiveDownload('m4a');
    } else if (this.#rmp.hasMp3 && (this.#rmp.okMp3 || this.#rmp.pdCheckCodecsDone)) {
      this.#runAudioProgressiveDownload('mp3');
    } else if (this.#rmp.hasOgg && (this.#rmp.okOgg || this.#rmp.pdCheckCodecsDone)) {
      this.#runAudioProgressiveDownload('ogg');
    } else {
      this.#rmp.rmpUtils.error(107);
      this.#rmp.rejectPromise('init', this.#rmp.errorData);
    }
  }

  #iosExternalCCFilesWebkitbeginfullscreen() {
    this.#rmp.dom.video.classList.remove('rmp-video');
  }

  #iosExternalCCFilesWebkitendfullscreen() {
    this.#rmp.dom.video.classList.add('rmp-video');
  }

  #initIosExternalCCFiles() {
    const typesPattern = /(captions|subtitles|subtitle|caption|text)/i;
    this.#rmp.dom.video.textTracks.addEventListener('addtrack', e => {
      if (e && e.track && e.track.kind) {
        if (typesPattern.test(e.track.kind)) {
          if (this.#rmp.ccFiles.length === (this.#ccFilesNativeTextTracksLoadedCount + 1)) {
            this.#rmp.loadedCCFiles = this.#rmp.ccFiles;
            this.#rmp.rmpCCManagement = new CCManagement(this.#rmp);
            this.#rmp.rmpCCManagement.appendUI();
          }
          this.#ccFilesNativeTextTracksLoadedCount++;
        }
      }
    });
    this.#rmp.dom.video.textTracks.addEventListener('change', () => {
      // update module UI when player is in fullcreen
      if (this.#rmp.isInFullscreen) {
        const captionList = this.#rmp.dom.captionsOverlayLevelsArea.querySelectorAll('.rmp-overlay-level');
        if (captionList.length > 0) {
          for (let i = 0, len = captionList.length; i < len; i++) {
            captionList[i].classList.remove('rmp-overlay-level-active');
          }
          for (let i = 0, len = this.#rmp.dom.video.textTracks.length; i < len; i++) {
            const track = this.#rmp.dom.video.textTracks[i];
            if (track && track.kind && typesPattern.test(track) && track.mode === 'showing') {
              captionList[i + 1].classList.add('rmp-overlay-level-active');
              return;
            }
          }
          captionList[0].classList.add('rmp-overlay-level-active');
        }
      }
    });

    this.iosAddTrackElements();
    this.wireIosExternalCCFiles();
  }

  #initLanguage() {
    // language labels
    // polyfill languages
    if (typeof navigator.languages === 'undefined') {
      Object.defineProperty(navigator, 'languages', {
        get: () => {
          // If the browser provides a single language (all that we've seen), then
          // make an array out of that.  Otherwise, return English.
          if (navigator.language) {
            return [navigator.language];
          }
          return ['en'];
        }
      });
    }
    const userLanguage = window.navigator.languages[0];
    const availableLanguages = [
      'arabic',
      'bengali',
      'chinese',
      'danish',
      'dutch',
      'english',
      'french',
      'finnish',
      'german',
      'greek',
      'hebrew',
      'hindi',
      'hungarian',
      'indonesian',
      'italian',
      'japanese',
      'korean',
      'norwegian',
      'polish',
      'portuguese',
      'romanian',
      'russian',
      'spanish',
      'swahili',
      'swedish',
      'taiwanese',
      'thai',
      'turkish',
      'ukrainian',
      'vietnamese'
    ];
    // #validatedLanguage + RTL for hebrew|arabic
    const RTL_LNG = /(hebrew|arabic)/i;
    if (Language.codes.taiwanese.includes(userLanguage)) {
      this.#validatedLanguage = 'taiwanese';
    } else {
      const userLanguageSplit = userLanguage.split('-');
      const coreLanguage = userLanguageSplit[0].toLowerCase();
      for (let i = 0, len = availableLanguages.length; i < len; i++) {
        if (Language.codes[availableLanguages[i]].includes(coreLanguage)) {
          this.#validatedLanguage = availableLanguages[i];
          if (RTL_LNG.test(this.#validatedLanguage)) {
            this.#rmp.rtl = true;
          }
          break;
        }
      }
    }
    let labels = Language[this.#validatedLanguage];
    if (this.#rmp.fixedLabelsLanguage) {
      if (availableLanguages.indexOf(this.#rmp.fixedLabelsLanguage) > -1) {
        labels = Language[this.#rmp.fixedLabelsLanguage];
      } else {
        labels = Language.english;
      }
    } else if (!FW.isEmptyObject(this.#rmp.labels) && this.#rmp.labels[this.#validatedLanguage] &&
      !FW.isEmptyObject(this.#rmp.labels[this.#validatedLanguage])) {
      let keys = Object.keys(Language[this.#validatedLanguage]);
      for (let i = 0, len = keys.length; i < len; i++) {
        let firstTierKeys = Object.keys(Language[this.#validatedLanguage][keys[i]]);
        if (typeof this.#rmp.labels[this.#validatedLanguage][keys[i]] === 'undefined') {
          continue;
        }
        firstTierKeys.forEach(firstTierKey => {
          if (typeof this.#rmp.labels[this.#validatedLanguage][keys[i]][firstTierKey] ===
            typeof Language[this.#validatedLanguage][keys[i]][firstTierKey]) {
            labels[keys[i]][firstTierKey] = this.#rmp.labels[this.#validatedLanguage][keys[i]][firstTierKey];
          }
        });
      }
    }
    this.#rmp.labels = labels;
    // apply custom labels if any for that language
    Logger.print('info', 'detected language is ' + this.#validatedLanguage + ' - labels follow:', this.#rmp.labels);
  }

  #onReplayLoadAdTag() {
    Logger.print('advertisement', 'attempt to reload adTag due to adTagReloadOnEnded|adScheduleReloadOnEnded setting');
    // this is needed for adScheduler update method when binding to ended event 
    if (!this.#rmp.adTagReloadOnEnded) {
      return;
    }
    if (this.#rmp.imaAdsManager) {
      this.#rmp.contentCompleteCalled = false;
    } else if (this.#rmp.rmpVast) {
      this.#rmp.rmpVast.contentPlayerCompleted = false;
    }
    let adTagUrl = this.#originalAdTagUrl;
    if (this.#rmp.rmpAdScheduler) {
      this.#rmp.adSchedule = this.#originalAdSchedule;
      this.#rmp.adTagWaterfall = [...this.#originalAdTagWaterfall];
      if (this.#rmp.emptyInitAdTagUrl) {
        this.#rmp.one('ended', this.#onEndedWaitForPlayFn);
        return;
      }
      adTagUrl = this.#rmp.adSchedule.preroll;
      this.#rmp.rmpAdScheduler.wireSchedulerEndedEvents();
    } else {
      this.#rmp.adTagWaterfall = [...this.#originalAdTagWaterfall];
      adTagUrl = this.#rmp.adTagUrl = this.#originalAdTagUrl;
    }
    this.#rmp.loadAds(adTagUrl, ['internal']).catch(error => {
      this.#rmp.rmpUtils.warning(1020, error);
      this.#rmp.rmpUtils.playPromise();
    });
    this.#rmp.one('ended', this.#onEndedWaitForPlayFn);
  }

  #onEndedWaitForPlay() {
    Logger.print('advertisement', 'adTagReloadOnEnded|adScheduleReloadOnEnded is set to true - wire #onEndedWaitForPlay');
    // this is needed for adScheduler update method when binding to ended event 
    if (!this.#rmp.adTagReloadOnEnded) {
      return;
    }
    this.#onReplayLoadAdTagFn = this.#onReplayLoadAdTag.bind(this);
    this.#rmp.one('play', this.#onReplayLoadAdTagFn);
  }

  #initAds() {
    // disable ad markers when chapters are on
    if (this.#rmp.chaptersLoc !== '') {
      this.#rmp.adShowMarkers = false;
    }
    if (this.#rmp.adParser !== 'rmp-vast') {
      this.#rmp.adParser = 'ima';
    }
    // no loop on iOS with ads
    if (Env.isIos[0] && this.#rmp.loopConfiguration) {
      this.#rmp.loopConfiguration = false;
    }
    // disables adTagReloadOnEnded with rmp-vast on iOS because ads loop with live
    /*if (Env.isApple &&
      (this.adParser === 'rmp-vast' || (this.adParser === 'ima' && this.adForceNativeFullscreenOnIosForAds))) {
      this.adTagReloadOnEnded = false;
    }*/
    if (this.#rmp.adImaDai) {
      this.#rmp.adParser = 'ima';
      this.#rmp.hlsEngine = 'hlsjs';
      this.#rmp.googleCast = false;
      this.#rmp.airplay = false;
      // we need adImaDaiLiveAssetKey or adImaDaiVodContentSourceId and adImaDaiVodVideoId
      if (this.#rmp.adImaDaiLiveAssetKey) {
        this.#imaDaiLive = true;
      } else if (this.#rmp.adImaDaiVodContentSourceId && this.#rmp.adImaDaiVodVideoId) {
        this.#imaDaiVod = true;
      } else if (this.#rmp.adImaDaiPodNetworkCode) {
        this.#imaDaiPod = true;
      } else {
        this.#rmp.ads = false;
      }
      // Apple devices we force native HLS as live with IMA DAI does not work with hls.js
      if ((this.#rmp.okHls && !this.#rmp.okHlsJS) || Env.isApple) {
        this.#imaDaiUseNativeHls = true;
      }
    } else {
      // outstream ads
      if (this.#rmp.adOutStream) {
        this.#rmp.src = {};
        this.#rmp.adSchedule = {};
      }
      // scheduler filter
      if (!FW.isEmptyObject(this.#rmp.adSchedule)) {
        AdScheduler.prepareMidRolls.call(this.#rmp);
        if (this.#rmp.adSchedule.preroll || this.#rmp.adSchedule.postroll || this.#rmp.scheduleHasMidRolls) {
          this.#rmp.rmpAdScheduler = new AdScheduler(this.#rmp);
          this.#rmp.rmpAdScheduler.prepareSchedule();
        }
      }
      // adsResponse
      if (this.#rmp.adTagUrl === '' && this.#rmp.adsResponse !== '' && !this.#rmp.rmpAdScheduler &&
        !this.#rmp.hasRelated && !this.#rmp.hasPlaylist) {
        this.#rmp.adTagUrl = this.#rmp.adsResponse;
      }
      // store initial value for ad outstream
      if ((this.#rmp.isVod || this.#rmp.adOutStream) && this.#rmp.adTagReloadOnEnded) {
        this.#originalAdTagUrl = this.#rmp.adTagUrl;
        this.#originalAdTagWaterfall = [...this.#rmp.adTagWaterfall];
        if (!this.#rmp.adOutStream) {
          if (this.#rmp.rmpAdScheduler) {
            this.#originalAdSchedule = {};
            this.#originalAdSchedule.preroll = this.#rmp.adSchedule.preroll;
            if (this.#rmp.scheduleHasMidRolls) {
              this.#originalAdSchedule.midroll = [...this.#rmp.adSchedule.midroll];
            }
            this.#originalAdSchedule.postroll = this.#rmp.adSchedule.postroll;
          }
          this.#onEndedWaitForPlayFn = this.#onEndedWaitForPlay.bind(this);
          this.#rmp.one('ended', this.#onEndedWaitForPlayFn);
        }
      }
      // emptyInitAdTagUrl
      if (!this.#rmp.hasRelated && !this.#rmp.hasPlaylist && !this.#rmp.rmpAdScheduler && this.#rmp.adTagUrl === '') {
        this.#rmp.emptyInitAdTagUrl = true;
      }
    }
  }

  #init360() {
    if (Env.ok360) {
      this.#rmp.dom.container.classList.add('rmp-360-ui');
      if (Env.isApple) {
        // disable ads for macOS Safari and rmp-vast
        if (this.#rmp.ads && this.#rmp.adParser === 'rmp-vast') {
          this.#rmp.ads = false;
        }
        if (Env.isIos[0]) {
          this.#rmp.fullscreenFullWindowMode = true;
        }
      }
      this.#rmp.rmpThreeSixty = new ThreeSixty(this.#rmp);
      this.#rmp.rmpThreeSixty.init();
    } else if (!FW.isEmptyObject(this.#rmp.video360FallbackSrc)) {
      Logger.print('360', 'using video360FallbackSrc');
      this.#rmp.src = this.#rmp.video360FallbackSrc;
      this.#rmp.video360 = false;
      InnerDefaults.resetHasStreamType.call(this.#rmp);
      this.#rmp.rmpUtils.detectSrc();
    } else {
      InnerDefaults.resetHasStreamType.call(this.#rmp);
      this.#rmp.video360 = false;
    }
  }

  #initAudioOnly() {
    this.#rmp.capLevelToPlayerSize = false;
    this.#rmp.ignoreDevicePixelRatio = false;
    this.#rmp.autoHeightMode = false;
    this.#rmp.height = 48;
    this.#rmp.pip = false;
    this.#rmp.skin = 's1';
    if (this.#rmp.skinBackgroundColor === '') {
      this.#rmp.skinBackgroundColor = '212121';
    }
    this.#rmp.dom.container.classList.add('rmp-audio-only');
    this.#rmp.labels.error.noSupportInstallChrome = '';
    this.#rmp.sharing = false;
    this.#rmp.contentMetadata.title = '';
    this.#rmp.seekBarThumbnailsLoc = '';
    this.#rmp.speed = false;
    if (this.#rmp.ads) {
      this.#rmp.adParser = 'ima';
    }
  }

  #initCustomIcons() {
    FW.ajax(this.#rmp.customIconsLoc, this.#rmp.ajaxWithCredentials, 'GET').
      then(data => {
        Logger.print('icons', 'loaded new JSON for custom icons at ' + this.#rmp.customIconsLoc);
        let jsonData;
        try {
          jsonData = JSON.parse(data);
          if (!FW.isEmptyObject(jsonData.icons)) {
            Logger.print('icons', 'parsed custom icons follow', jsonData.icons);
            UIManagement.appendCSSForCustomIcons(
              jsonData.icons,
              this.#rmp.adOutStream,
              this.#rmp.tvPlayerSkin
            );
          }
        } catch (event) {
          this.#rmp.rmpUtils.warning(9000, event);
        }
      }).catch(event => {
        this.#rmp.rmpUtils.warning(9001, event);
      });
  }

  #initMatomoTracking() {
    let dataMatomoTitle;
    if (this.#rmp.dataMatomoTitle) {
      dataMatomoTitle = this.#rmp.dataMatomoTitle;
    } else if (this.#rmp.contentMetadata.title) {
      dataMatomoTitle = this.#rmp.contentMetadata.title;
    }
    this.#rmp.dom.video.setAttribute('data-matomo-title', dataMatomoTitle);
  }

  #initFullscreenManagement() {
    this.#rmp.hasNativeFullscreenSupport = Env.hasNativeFullscreenSupport;
    if (this.#rmp.ads) {
      if (Env.isIos[0]) {
        if (this.#rmp.adParser === 'ima' && !this.#rmp.adForceNativeFullscreenOnIosForAds) {
          this.#rmp.fullscreenFullWindowMode = true;
          this.#rmp.adDisableCustomPlaybackForIOS10Plus = true;
        } else if (this.#rmp.adParser === 'rmp-vast') {
          this.#rmp.fullscreenFullWindowMode = true;
        }
      }
    }
    if (Env.isInIframe) {
      this.#rmp.fullscreenFullWindowMode = false;
      this.#rmp.adDisableCustomPlaybackForIOS10Plus = false;
    }
    if (this.#rmp.fullscreenFullWindowMode) {
      this.#rmp.hasNativeFullscreenSupport = false;
    }
    if (!this.#rmp.isMobile || !this.#rmp.hasNativeFullscreenSupport) {
      this.#rmp.automaticFullscreenOnLandscape = false;
      this.#rmp.automaticLandscapeOnFullScreen = false;
    }
  }

  #initConfig() {
    // resize needed here for playlist/related
    // resize
    this.#rmp.rmpSizeManagement.initialSizing();

    // VttJS
    this.#rmp.dom.ccItems = [];
    if (FW.isValidCCFiles(this.#rmp.ccFiles)) {
      if (Env.isIos[0]) {
        this.#rmp.hasExternalCCFiles = true;
        this.#rmp.dom.container.classList.add('rmp-captions-external');
      } else {
        this.#rmp.rmpVttJS = new VttJS(this.#rmp, this.#rmp.ccFiles);
      }
      if (this.#rmp.autoBumpCaptionsOnVisibleControlBar) {
        let style = '';
        style += '.rmp-chrome.rmp-s1 .rmp-cc-area {bottom: 48px}';
        style += '.rmp-no-chrome.rmp-s1 .rmp-cc-area {bottom: 0}';
        style += '.rmp-chrome.rmp-s2 .rmp-cc-area {bottom: 54px}';
        style += '.rmp-no-chrome.rmp-s2 .rmp-cc-area {bottom: 0}';
        FW.appendStyle(style, this.#rmp.shadowRoot);
      }
    }

    // Language & labels
    this.#initLanguage();

    // audio only
    if (this.#rmp.audioOnly) {
      this.#initAudioOnly();
    }

    // mediaSession metadata
    this.#rmp.contentMetadata = FW.filterContentMetadata(this.#rmp.contentMetadata);
    Logger.print('metadata', 'filtered content metadata follows', this.#rmp.contentMetadata);

    // createVideoTag
    this.createVideoTag();

    // apply color
    this.#rmp.rmpColorsManagement.applySkinColors();

    // preload
    if (this.#rmp.isMobile) {
      this.#rmp.dom.container.classList.add('rmp-mobile');
    }

    // customIconsLoc
    if (this.#rmp.customIconsLoc !== '') {
      this.#initCustomIcons();
    }

    // ads
    if (this.#rmp.ads) {
      this.#initAds();
    }

    // adblock 
    if (this.#rmp.adOnAdBlockPreventPlayback) {
      this.#rmp.rmpAdBlocker = new AdBlocker(this.#rmp);
      this.#rmp.rmpAdBlocker.initAdBlockDetectionPreventPlayback();
    }

    // viewable 
    if (Env.hasIntersectionObserver) {
      this.#rmp.rmpViewableObserver = new ViewableObserver(this.#rmp);
      this.#rmp.rmpViewableObserver.init();
    }

    // detect available streaming protocols
    // for playlist/related this is done after JSON has loaded
    if (!this.#rmp.hasRelated && !this.#rmp.hasPlaylist && !this.#rmp.adOutStream) {
      this.#rmp.rmpUtils.detectSrc();
    }

    // sharing
    if (this.#rmp.sharing && this.#rmp.sharingUrl === '') {
      this.#rmp.sharingUrl = document.URL;
    }

    // 360
    if (this.#rmp.video360) {
      this.#init360();
    }

    // FS 
    this.#initFullscreenManagement();

    // poster
    if (!this.#rmp.audioOnly) {
      this.#rmp.rmpPosterManagement.filterPoster();
    }

    // Matomo
    if (this.#rmp.enableMatomoTracking) {
      this.#initMatomoTracking();
    }

    // set up initial accessibility features
    this.#rmp.rmpAccessible.init();

    // start log management here
    this.#rmp.rmpLogsManagement.init();

    // start Google Analytics
    if (this.#rmp.rmpGoogleAnalytics) {
      this.#rmp.rmpGoogleAnalytics.init();
    }

    // related
    if (this.#rmp.hasRelated) {
      if (this.#rmp.relatedAudioOnly) {
        this.#rmp.dom.container.classList.add('rmp-related-audio-only');
      }
      this.#rmp.rmpRelated.init();
      return;
    }

    // playlist
    if (this.#rmp.hasPlaylist) {
      if (this.#rmp.playlistAudioOnly) {
        this.#rmp.dom.container.classList.add('rmp-playlist-audio-only');
      }
      this.#rmp.rmpPlaylist.init();
      return;
    }

    // License is valid and this is NOT playlist and NOT related > classic selectStreamingProtocol
    this.selectStreamingProtocol();
  }

  iosAddTrackElements() {
    this.#rmp.ccFiles.forEach(ccFile => {
      const trackElement = document.createElement('track');
      trackElement.setAttribute('kind', 'captions');
      trackElement.setAttribute('srclang', ccFile.lng);
      trackElement.setAttribute('label', ccFile.name);
      trackElement.setAttribute('src', ccFile.uri);
      this.#rmp.dom.video.appendChild(trackElement);
    });
  }

  #managePreloadForAdsOnMobile() {
    if (this.#rmp.ads) {
      if (this.#rmp.adParser === 'rmp-vast' && this.#rmp.isMobile) {
        // on Android we must set preload none with rmp-vast or we get a streaming error 
        this.#rmp.preload = 'none';
      } else if (this.#rmp.adParser === 'ima' && Env.isIos[0]) {
        // fullscreen issue on iOS with IMA (click FS button before play and FS no longer works)
        this.#rmp.preload = 'none';
      }
    }
  }

  selectStreamingProtocol() {
    Logger.print('info', `starting selectStreamingProtocol() at ${(FW.getNow() - this.#rmp.refTime)} ms`);
    this.#managePreloadForAdsOnMobile();
    if (this.#rmp.adOutStream) {
      Logger.print('info', 'select outstream ads');
      this.#runOutStream();
    } else if (this.#rmp.hasDash && this.#rmp.okShaka && this.#rmp.dashFirst) {
      Logger.print('info', 'select MPEG-DASH with Shaka player with dashFirst true');
      this.#rmp.readingDashShaka = true;
      this.#rmp.readingShaka = true;
      this.#rmp.startingSrcUri = this.#rmp.src.dash;
      this.#rmp.rmpDomManagement.prepare();
      this.#rmp.rmpDomManagement.append();
    } else if (this.#rmp.hasFps && this.#rmp.okFps) {
      Logger.print('info', 'select native HLS with FairPlay protection');
      this.#runNativeHls('fps');
    } else if (this.#rmp.hasHls && this.#rmp.okHlsJS && this.#rmp.hlsEngine === 'hlsjs') {
      Logger.print('info', 'select HLS with hls.js');
      this.#rmp.readingHlsJS = true;
      this.#rmp.startingSrcUri = this.#rmp.src.hls;
      this.#rmp.rmpDomManagement.prepare();
      this.#rmp.rmpDomManagement.append();
    } else if (this.#rmp.hasHls && this.#rmp.okShaka && this.#rmp.hlsEngine === 'shakaplayer') {
      Logger.print('info', 'select HLS with Shaka player');
      this.#rmp.readingHlsShaka = true;
      this.#rmp.readingShaka = true;
      this.#rmp.startingSrcUri = this.#rmp.src.hls;
      this.#rmp.rmpDomManagement.prepare();
      this.#rmp.rmpDomManagement.append();
    } else if (this.#rmp.hasHls && this.#rmp.okHls) {
      Logger.print('info', 'select native HLS');
      this.#runNativeHls('hls');
    } else if (this.#rmp.hasDash && this.#rmp.okShaka) {
      Logger.print('info', 'select MPEG-DASH with Shaka player');
      this.#rmp.readingDashShaka = true;
      this.#rmp.readingShaka = true;
      this.#rmp.startingSrcUri = this.#rmp.src.dash;
      this.#rmp.rmpDomManagement.prepare();
      this.#rmp.rmpDomManagement.append();
    } else {
      if (this.#rmp.audioOnly) {
        Logger.print('info', 'select HTML5 audio player');
        this.#runHtml5Audio();
      } else {
        if (this.#rmp.hasMp4 && (this.#rmp.okMp4 || this.#rmp.pdCheckCodecsDone)) {
          Logger.print('info', 'select HTML5 video player with MP4 video');
          this.#runMp4();
        } else if (this.#rmp.hasWebM && (this.#rmp.okWebM || this.#rmp.pdCheckCodecsDone)) {
          Logger.print('info', 'select HTML5 video player with WebM video');
          this.#runWebM();
        } else if (this.#rmp.playlistAudioOnly || this.#rmp.relatedAudioOnly || this.#rmp.audioOnlyUseVideoLayout) {
          Logger.print('info', 'select HTML5 audio player with video layout with ogg/mp3/m4a');
          this.#runHtml5Audio();
        } else {
          this.#rmp.rmpUtils.error(107);
          this.#rmp.rejectPromise('init', this.#rmp.errorData);
        }
      }
    }
  }

  postAutoplayDetection() {
    // handle related
    if (this.#rmp.relatedLoc !== '' || (Array.isArray(this.#rmp.relatedData) && this.#rmp.relatedData[0])) {
      this.#rmp.hasRelated = true;
      this.#rmp.rmpRelated = new Related(this.#rmp);
      if (this.#rmp.relatedLoc !== '' && Array.isArray(this.#rmp.relatedData) && this.#rmp.relatedData[0]) {
        this.#rmp.relatedLoc = '';
      }
      if (!this.#rmp.srcChangeAutoplay) {
        this.#rmp.relatedUpNextAutoplay = false;
      }
      if (this.#rmp.relatedUpNextAutoplay) {
        this.#rmp.loopConfiguration = false;
      }
    }
    // handle playlist
    if (this.#rmp.playlistLoc !== '' || (Array.isArray(this.#rmp.playlistData) && this.#rmp.playlistData[0])) {
      this.#rmp.hasPlaylist = true;
      this.#rmp.rmpPlaylist = new Playlist(this.#rmp);
      if (this.#rmp.playlistLoc !== '' && Array.isArray(this.#rmp.playlistData) && this.#rmp.playlistData[0]) {
        this.#rmp.playlistLoc = '';
      }
      this.#rmp.autoHeightMode = true;
      if (!this.#rmp.srcChangeAutoplay) {
        this.#rmp.playlistUpNextAutoplay = false;
      }
      if (this.#rmp.playlistUpNextAutoplay) {
        this.#rmp.loopConfiguration = false;
      }
    }

    if (this.#rmp.hasPlaylist || this.#rmp.hasRelated) {
      this.#rmp.audioOnly = false;
      this.#rmp.adTagReloadOnEnded = false;
      this.#rmp.adSchedule = {};
      this.#rmp.chaptersLoc = '';
    }

    if (this.#rmp.transcripts[0] && this.#rmp.transcripts[0].lng && this.#rmp.transcripts[0].name &&
      this.#rmp.transcripts[0].uri) {
      this.#hasTranscripts = true;
      this.#rmp.autoHeightMode = true;
    }

    this.#rmp.bitrateDataDisplayed = this.#rmp.bitrateDataDisplayed.toLowerCase();

    if (this.#rmp.audioOnlyUseVideoLayout) {
      this.#rmp.pip = false;
    }

    // init config;
    this.#initConfig();
  }

  wireIosExternalCCFiles() {
    this.#iosExternalCCFilesWebkitbeginfullscreenFn = this.#iosExternalCCFilesWebkitbeginfullscreen.bind(this);
    this.#iosExternalCCFilesWebkitendfullscreenFn = this.#iosExternalCCFilesWebkitendfullscreen.bind(this);
    this.#rmp.dom.video.addEventListener('webkitbeginfullscreen', this.#iosExternalCCFilesWebkitbeginfullscreenFn);
    this.#rmp.dom.video.addEventListener('webkitendfullscreen', this.#iosExternalCCFilesWebkitendfullscreenFn);
  }

  unwireIosExternalCCFiles() {
    this.#rmp.dom.video.removeEventListener('webkitbeginfullscreen', this.#iosExternalCCFilesWebkitbeginfullscreenFn);
    this.#rmp.dom.video.removeEventListener('webkitendfullscreen', this.#iosExternalCCFilesWebkitendfullscreenFn);
  }

  unwireHtml5VideoEvents() {
    const video = this.#rmp.dom.video;
    video.removeEventListener('durationchange', this.#durationchangeFn);
    video.removeEventListener('loadedmetadata', this.#loadedmetadataFn);
    video.removeEventListener('waiting', this.#waitingFn);
    video.removeEventListener('pause', this.#pauseFn);
    video.removeEventListener('play', this.#playFn);
    video.removeEventListener('timeupdate', this.#timeupdateFn);
  }

  unwireInitAdEvents() {
    Logger.print('advertisement', 'unwireInitAdEvents');
    this.#rmp.off('play', this.#onReplayLoadAdTagFn);
    this.#rmp.off('ended', this.#onEndedWaitForPlayFn);
  }

  createVideoTag() {
    // video or  audio tag
    if (this.#rmp.audioOnly) {
      this.#rmp.dom.video = document.createElement('audio');
    } else {
      this.#rmp.dom.video = document.createElement('video');
    }
    if (this.#rmp.hasExternalCCFiles && Env.isIos[0]) {
      this.#initIosExternalCCFiles();
    }

    const video = this.#rmp.dom.video;

    // object-fit 
    if (FW.cssPropertySupport('objectFit') && !this.#rmp.audioOnly) {
      switch (this.#rmp.scaleMode) {
        case 'letterbox':
          video.classList.add('rmp-object-fit-contain');
          break;
        case 'zoom':
          video.classList.add('rmp-object-fit-cover');
          break;
        case 'stretch':
          video.classList.add('rmp-object-fit-fill');
          break;
        case 'none':
          video.classList.add('rmp-object-fit-none');
          break;
        default:
          video.classList.add('rmp-object-fit-contain');
          break;
      }
    }

    // add CSS class
    video.classList.add('rmp-video');
    video.controls = false;
    if (this.#rmp.loopConfiguration) {
      video.loop = 'loop';
    }
    if (this.#rmp.crossorigin !== '') {
      video.setAttribute('crossorigin', this.#rmp.crossorigin);
    }
    video.setAttribute('x-webkit-airplay', 'allow');
    video.setAttribute('controlslist', 'nodownload');
    if (this.#rmp.isMobile) {
      video.setAttribute('playsinline', true);
      video.setAttribute('webkit-playsinline', true);
    }
    if (this.#rmp.isMobile && this.#rmp.contentMetadata.title) {
      video.setAttribute('title', this.#rmp.contentMetadata.title);
    }
    /*if (this.#rmp.readingHls) {
    video.setAttribute('type', 'application/vnd.apple.mpegurl');
    }*/
    /*if (this.#rmp.pipAuto) {
      video.setAttribute('autopictureinpicture', true);
    }*/
    // tvPlayerSkin is for Android TV as we cannot tell difference between Android mobile and Android TV
    if (Env.isAndroidMobileOrTabletWebView || this.#rmp.tvPlayerSkin) {
      video.poster = FW.blackFrame;
    }

    // events
    this.#durationchangeFn = this.#rmp.rmpEventsManagement.durationchangeCallback.bind(this.#rmp.rmpEventsManagement);
    video.addEventListener('durationchange', this.#durationchangeFn);

    this.#loadedmetadataFn = this.#rmp.rmpEventsManagement.loadedmetadataCallback.bind(this.#rmp.rmpEventsManagement);
    video.addEventListener('loadedmetadata', this.#loadedmetadataFn);

    this.#waitingFn = this.#rmp.rmpEventsManagement.waitingCallback.bind(this.#rmp.rmpEventsManagement);
    video.addEventListener('waiting', this.#waitingFn);

    this.#rmp.endedFn = this.#rmp.rmpEventsManagement.endedCallback.bind(this.#rmp.rmpEventsManagement);
    if (this.#rmp.isVod) {
      video.addEventListener('ended', this.#rmp.endedFn);
    }
    this.#rmp.rmpEventsManagement.attachAuxiliaryEvents();

    this.#pauseFn = this.#rmp.rmpEventsManagement.pauseCallback.bind(this.#rmp.rmpEventsManagement);
    video.addEventListener('pause', this.#pauseFn);

    this.#playFn = this.#rmp.rmpEventsManagement.playCallback.bind(this.#rmp.rmpEventsManagement);
    video.addEventListener('play', this.#playFn);

    // Update the seek bar as the video plays
    this.#timeupdateFn = this.#rmp.rmpEventsManagement.timeupdateCallback.bind(this.#rmp.rmpEventsManagement);
    video.addEventListener('timeupdate', this.#timeupdateFn);

    // mediaSession
    if (typeof window.navigator.mediaSession !== 'undefined') {
      this.#rmp.rmpMediaSessionMetadata = new MediaSessionMetadata(this.#rmp);
      this.#rmp.rmpMediaSessionMetadata.init();
    }
  }

}
